import type { BrevErrorCode } from "server/config";

export type CreationFailure = {
  name: string;
  code?: BrevErrorCode;
  status?: number;
};

export type CreationErrorGuidance = {
  message: string;
  severity: "error" | "warning";
  action?: "billing";
};

export function creationErrorGuidance(
  failures: CreationFailure[],
  total: number,
  canViewBilling: boolean,
): CreationErrorGuidance {
  const first = failures[0];

  switch (first?.code) {
    case "WORKSPACE_ALREADY_EXISTS": {
      const names = failures
        .filter((failure) => failure.code === "WORKSPACE_ALREADY_EXISTS")
        .map((failure) => failure.name);
      return {
        message:
          names.length > 1
            ? `Instances named "${names.join('", "')}" already exist. Choose different names.`
            : `An instance named "${names[0]}" already exists. Choose a different name.`,
        severity: "error",
      };
    }
    case "PAYMENT_METHOD_REQUIRED":
      return canViewBilling
        ? {
            message:
              "Add a payment method in your organization billing settings.",
            severity: "warning",
            action: "billing",
          }
        : {
            message: "Ask your organization admin to add a payment method.",
            severity: "warning",
          };
    case "CREDIT_EXHAUSTED":
      return canViewBilling
        ? {
            message:
              "This organization has run out of credits. Review billing to continue.",
            severity: "warning",
            action: "billing",
          }
        : {
            message: "Ask your organization admin to review billing credits.",
            severity: "warning",
          };
    case "QUOTA_EXCEEDED":
      return {
        message:
          "Your cloud account has reached a compute quota. Choose a smaller instance or request a quota increase.",
        severity: "warning",
      };
    case "CAPACITY_UNAVAILABLE":
      return {
        message:
          "This instance is unavailable in the selected region. Try another region or instance type.",
        severity: "warning",
      };
    default:
      if (first?.status === 504) {
        return {
          message:
            "The request timed out. Check your instances before retrying.",
          severity: "warning",
        };
      }
      return {
        message:
          total > 1
            ? `${failures.length} of ${total} instances failed. Try again.`
            : "Unable to create instance. Try again.",
        severity: "error",
      };
  }
}
